import { Audience } from './audience'
import { LoanStatus } from './loanStatus'

export type IndividualStep = {
  label: string
  value: string
  includedStatuses: string[]
  stepNumber: number
}
export type ActiveStepInfo = {
  stepNumber: number
  stepLabel: string
  stepValue: string
}

// Creating constants for timeline steps that will affect the UI so that we can easily import them into the proper components and keep the values consistent
export const ESTIMATE = 'estimate'
export const APP_SUBMITTED = 'app_submitted'
export const PRE_APPROVED = 'pre_approved'
export const UNDER_CONTRACT = 'under_contract'
export const FINAL_APPROVED = 'final_approved'
export const CLEARED_TO_CLOSE = 'cleared_to_close'
export const FUNDED = 'funded'
export const COMPLETED = 'completed'
export const NOT_ELIGIBLE = 'not_eligible'
export const IN_REVIEW = 'in_review'
export const UNKNOWN = 'unknown'
export const CORRESPONDENT_IN_PROGRESS = 'correspondent_in_progress'
export const CORRESPONDENT_COMPLETED = 'correspondent_completed'

// Note: Timeline steps start at 1. All unknown statuses will be set to 0 and will not be displayed in the timeline.
export const UNKNOWN_INFO: IndividualStep = {
  label: 'Unknown',
  value: UNKNOWN,
  includedStatuses: ['unknown'],
  stepNumber: 0,
}
export const NOT_ELIGIBLE_STATUSES: IndividualStep[] = [
  {
    label: 'Not Eligible',
    value: NOT_ELIGIBLE,
    includedStatuses: [LoanStatus.NOT_ELIGIBLE],
    stepNumber: 0,
  },
  {
    label: 'In Review',
    value: IN_REVIEW,
    includedStatuses: [LoanStatus.IN_REVIEW],
    stepNumber: 0,
  },
]
export const LENDER_TIMELINE: IndividualStep[] = [
  {
    label: 'Estimate',
    value: ESTIMATE,
    includedStatuses: [LoanStatus.ELIGIBLE],
    stepNumber: 1,
  },
  {
    label: 'App Submitted',
    value: APP_SUBMITTED,
    includedStatuses: [LoanStatus.SUBMITTED],
    stepNumber: 2,
  },
  {
    label: 'Pre-Approved',
    value: PRE_APPROVED,
    includedStatuses: [LoanStatus.PRE_APPROVED],
    stepNumber: 3,
  },
  {
    label: 'Under Contract',
    value: UNDER_CONTRACT,
    includedStatuses: [LoanStatus.UNDER_CONTRACT],
    stepNumber: 4,
  },
  {
    label: 'Final Approved',
    value: FINAL_APPROVED,
    includedStatuses: [LoanStatus.FINAL_APPROVED],
    stepNumber: 5,
  },
  {
    label: 'Cleared to Close',
    value: CLEARED_TO_CLOSE,
    includedStatuses: [LoanStatus.CLEARED_TO_CLOSE],
    stepNumber: 6,
  },
  {
    label: 'Funded',
    value: FUNDED,
    includedStatuses: [LoanStatus.FUNDED],
    stepNumber: 7,
  },
  {
    label: 'Completed',
    value: COMPLETED,
    includedStatuses: [LoanStatus.COMPLETED],
    stepNumber: 9,
  },
]
export const AGENT_TIMELINE: IndividualStep[] = [
  {
    label: 'Estimate',
    value: ESTIMATE,
    includedStatuses: [LoanStatus.ELIGIBLE],
    stepNumber: 1,
  },
  {
    label: 'App Submitted',
    value: APP_SUBMITTED,
    includedStatuses: [LoanStatus.SUBMITTED],
    stepNumber: 2,
  },
  {
    label: 'Pre-Approved',
    value: PRE_APPROVED,
    includedStatuses: [LoanStatus.PRE_APPROVED],
    stepNumber: 3,
  },
  {
    label: 'Under Contract',
    value: UNDER_CONTRACT,
    includedStatuses: [LoanStatus.UNDER_CONTRACT, LoanStatus.FINAL_APPROVED],
    stepNumber: 4,
  },
  {
    label: 'Closing',
    value: CLEARED_TO_CLOSE,
    includedStatuses: [LoanStatus.CLEARED_TO_CLOSE],
    stepNumber: 5,
  },
  {
    label: 'Selling',
    value: FUNDED,
    includedStatuses: [LoanStatus.FUNDED],
    stepNumber: 6,
  },
  {
    label: 'Completed',
    value: COMPLETED,
    includedStatuses: [LoanStatus.COMPLETED],
    stepNumber: 7,
  },
]

export const CUSTOMER_TIMELINE: IndividualStep[] = [
  {
    label: 'App Submitted',
    value: APP_SUBMITTED,
    includedStatuses: [LoanStatus.SUBMITTED],
    stepNumber: 1,
  },
  {
    label: 'Pre-Approved',
    value: PRE_APPROVED,
    includedStatuses: [LoanStatus.PRE_APPROVED],
    stepNumber: 2,
  },
  {
    label: 'Under Contract',
    value: UNDER_CONTRACT,
    includedStatuses: [LoanStatus.UNDER_CONTRACT, LoanStatus.FINAL_APPROVED],
    stepNumber: 3,
  },
  {
    label: 'Closing',
    value: CLEARED_TO_CLOSE,
    includedStatuses: [LoanStatus.CLEARED_TO_CLOSE],
    stepNumber: 4,
  },
  {
    label: 'Selling',
    value: FUNDED,
    includedStatuses: [LoanStatus.FUNDED],
    stepNumber: 5,
  },
  {
    label: 'Completed',
    value: COMPLETED,
    includedStatuses: [LoanStatus.COMPLETED],
    stepNumber: 6,
  },
]

export const getTimelineLabels = (audience: Audience): IndividualStep[] => {
  const timelineLabels =
    audience === Audience.HOMEBUYER
      ? CUSTOMER_TIMELINE
      : audience === Audience.LENDER
        ? LENDER_TIMELINE
        : AGENT_TIMELINE
  return timelineLabels
}
export const getActiveTimelineStep = ({
  audience,
  status,
  isCorrespondentLoan = false,
}: {
  audience: Audience
  status: LoanStatus
  isCorrespondentLoan?: boolean
}): ActiveStepInfo => {
  if (isCorrespondentLoan) {
    if (status === LoanStatus.COMPLETED) {
      return {
        stepNumber: 0,
        stepLabel: 'Correspondent Loan Completed',
        stepValue: CORRESPONDENT_COMPLETED,
      }
    }
    return {
      stepNumber: 0,
      stepLabel: 'Correspondent Loan In Progress',
      stepValue: CORRESPONDENT_IN_PROGRESS,
    }
  }

  const timelineLabels = getTimelineLabels(audience).concat(NOT_ELIGIBLE_STATUSES)
  const activeStep = timelineLabels.find((step) => step.includedStatuses.includes(status)) || UNKNOWN_INFO
  return {
    stepNumber: activeStep.stepNumber,
    stepLabel: activeStep.label,
    stepValue: activeStep.value,
  }
}
